import { useState, useEffect, useRef } from "react";
import { Play, Pause, Volume2, VolumeX, Layers, Mic, Music, FileAudio } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MultiLayerPlayer = ({ episode }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volumes, setVolumes] = useState({ narrator: 1, music: 0.3, sfx: 0.5 });
  const [muted, setMuted] = useState({ narrator: false, music: false, sfx: false });
  const [showMixer, setShowMixer] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const narratorRef = useRef(null);
  const musicRef = useRef(null);
  const sfxRef = useRef(null);

  const hasAudio = episode.mixed_audio_url || episode.narrator_track_url || episode.music_bed_url || episode.sfx_url || episode.audio_url;
  const hasMixedAudio = !!episode.mixed_audio_url;
  const fallbackNarrator = !episode.narrator_track_url && !hasMixedAudio && episode.audio_url;

  useEffect(() => {
    const primary = narratorRef.current;
    if (!primary) return;
    const handleTime = () => setCurrentTime(primary.currentTime);
    const handleMeta = () => setDuration(primary.duration);
    const handleEnd = () => setIsPlaying(false);
    primary.addEventListener("timeupdate", handleTime);
    primary.addEventListener("loadedmetadata", handleMeta);
    primary.addEventListener("ended", handleEnd);
    return () => {
      primary.removeEventListener("timeupdate", handleTime);
      primary.removeEventListener("loadedmetadata", handleMeta);
      primary.removeEventListener("ended", handleEnd);
    };
  }, [hasAudio]);

  const togglePlay = () => {
    if (isPlaying) {
      narratorRef.current?.pause();
      musicRef.current?.pause();
      sfxRef.current?.pause();
    } else {
      narratorRef.current?.play();
      musicRef.current?.play();
      sfxRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const updateVolume = (layer, value) => {
    setVolumes(prev => ({ ...prev, [layer]: value }));
    setMuted(prev => ({ ...prev, [layer]: false }));
    if (layer === "narrator" && narratorRef.current) narratorRef.current.volume = value;
    if (layer === "music" && musicRef.current) musicRef.current.volume = value;
    if (layer === "sfx" && sfxRef.current) sfxRef.current.volume = value;
  };

  const toggleMute = (layer) => {
    const newMuted = !muted[layer];
    setMuted(prev => ({ ...prev, [layer]: newMuted }));
    const ref = layer === "narrator" ? narratorRef : layer === "music" ? musicRef : sfxRef;
    if (ref.current) ref.current.volume = newMuted ? 0 : volumes[layer];
  };

  const seekTo = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const time = pct * duration;
    if (narratorRef.current) narratorRef.current.currentTime = time;
    if (musicRef.current) musicRef.current.currentTime = Math.min(time, musicRef.current.duration || time);
    if (sfxRef.current) sfxRef.current.currentTime = Math.min(time, sfxRef.current.duration || time);
  };

  const formatTime = (t) => {
    if (!t || isNaN(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  if (!hasAudio) {
    return (
      <div className="bg-white/5 p-4 rounded text-center text-gray-500" data-testid="multi-layer-player-empty">
        <FileAudio size={24} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">No audio layers uploaded yet</p>
      </div>
    );
  }

  const layers = [
    { key: "narrator", label: "Narrator", icon: Mic, color: "blue", url: episode.narrator_track_url, ref: narratorRef, loop: false },
    { key: "music", label: "Music Bed", icon: Music, color: "purple", url: episode.music_bed_url, ref: musicRef, loop: true },
    { key: "sfx", label: "SFX", icon: Volume2, color: "green", url: episode.sfx_url, ref: sfxRef, loop: false },
  ].filter(l => l.url);

  return (
    <div className="bg-black/30 border border-white/10 rounded-lg overflow-hidden" data-testid="multi-layer-player">
      {layers.map(l => (
        <audio key={l.key} ref={l.ref} src={`${BACKEND_URL}${l.url}`} loop={l.loop} preload="metadata" />
      ))}
      {hasMixedAudio && <audio ref={narratorRef} src={`${BACKEND_URL}${episode.mixed_audio_url}`} preload="metadata" />}
      {fallbackNarrator && <audio ref={narratorRef} src={`${BACKEND_URL}${episode.audio_url}`} preload="metadata" />}

      <div className="p-4">
        <div className="flex items-center gap-4">
          <button onClick={togglePlay} className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors flex-shrink-0" data-testid="mlp-play-btn">
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-semibold text-sm">Multi-Layer Audio</p>
              <div className="flex gap-1">
                {layers.map(l => (
                  <span key={l.key} className={`text-xs px-1.5 py-0.5 rounded bg-${l.color}-500/20 text-${l.color}-400`}>{l.label}</span>
                ))}
                {fallbackNarrator && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Main</span>}
                {hasMixedAudio && <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">Mixed + SFX</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-mono w-10">{formatTime(currentTime)}</span>
              <div className="flex-1 h-1.5 bg-gray-800 rounded-full cursor-pointer relative group" onClick={seekTo} data-testid="mlp-seek-bar">
                <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
                <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ left: `${duration ? (currentTime / duration) * 100 : 0}%` }} />
              </div>
              <span className="text-xs text-gray-500 font-mono w-10 text-right">{formatTime(duration)}</span>
            </div>
          </div>
          <button onClick={() => setShowMixer(!showMixer)} className="p-2 hover:bg-white/10 rounded transition-colors" data-testid="mlp-mixer-toggle">
            <Layers size={20} className="text-gray-400" />
          </button>
        </div>
      </div>

      {showMixer && (
        <div className="px-4 pb-4 space-y-2 border-t border-white/5 pt-3" data-testid="mlp-mixer">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">Layer Mixer</p>
          {(fallbackNarrator ? [{ key: "narrator", label: "Main Audio", icon: Mic, color: "blue" }] : []).concat(layers).map(l => (
            <div key={l.key} className="flex items-center gap-3 py-1" data-testid={`mlp-layer-${l.key}`}>
              <button onClick={() => toggleMute(l.key)} className={`p-1 rounded hover:bg-white/10 ${muted[l.key] ? "opacity-40" : ""}`} data-testid={`mlp-mute-${l.key}`}>
                {muted[l.key] ? <VolumeX size={16} className="text-gray-600" /> : <l.icon size={16} className={`text-${l.color}-400`} />}
              </button>
              <span className={`text-sm w-20 ${muted[l.key] ? "line-through text-gray-600" : "text-gray-300"}`}>{l.label}</span>
              <input
                type="range" min="0" max="1" step="0.05"
                value={muted[l.key] ? 0 : volumes[l.key]}
                onChange={(e) => updateVolume(l.key, parseFloat(e.target.value))}
                className={`flex-1 accent-${l.color}-500`}
                data-testid={`mlp-vol-${l.key}`}
              />
              <span className="text-xs w-10 text-right text-gray-500 font-mono">{muted[l.key] ? "MUTE" : `${Math.round(volumes[l.key] * 100)}%`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiLayerPlayer;
